<template>
  <BittsModalTwo
    class="c-population-sharing-modal"
    :title="title"
    :use-mask-to-close="true"
    :loading="initialLoading || isLoading"
    :disabled="initialLoading || isLoading || !hasChanges"
    @closed="handleClose"
    @action="handleSave"
  >
    <div class="flex flex-col gap-20 py-20">
      <!-- Sharing Section -->
      <div class="flex justify-between">
        <h3 class="text-neutral-text">Share your population</h3>
        <BittsSwitch v-model="isShared" @change="toggleShared" />
      </div>

      <!-- Sharing Visibility Section -->
      <div v-if="isShared" class="flex justify-between">
        <h3 class="text-neutral-text">What will be shared</h3>
        <BittsSelect
          v-model="selectedSharing"
          class="c-sharing-settings-item__sharing"
          :class="{
            'c-sharing-settings-item__sharing--overlap-counts': isOverlapCounts,
          }"
          dropdown-class-name="c-sharing-settings-item__sharing--dropdown"
          list-height="300"
          :dropdown-match-select-width="false"
          :options="sharingSelectOptions"
          :searchable="false"
          @change="onVisibilityChange"
          :disabled="!hasSharingPermission"
        >
          <template #option="{ option }">
            <div class="flex flex-col">
              <div
                class="c-sharing-settings-item__sharing-option-label"
                :class="{
                  'text-warning-text':
                    option.value === SHARING_TYPES.OLAPS_ONLY,
                  'text-info-text': option.value !== SHARING_TYPES.OLAPS_ONLY,
                }"
              >
                {{ option.label }}
              </div>
              <div class="c-sharing-settings-item__sharing-option-description">
                {{ option.description }}
              </div>
            </div>
          </template>
        </BittsSelect>
      </div>

      <!-- Preset Fields Section -->
      <div v-if="isShared && !isOverlapCounts" class="flex justify-between">
        <h3 class="text-neutral-text">Fields you will be sharing</h3>
        <BittsSelect
          ref="presetSelectRef"
          v-model="selectedPresetId"
          :has-opt-groups="true"
          class="c-sharing-settings-item__preset"
          dropdown-class-name="c-sharing-settings-item__preset-dropdown"
          :dropdown-match-select-width="false"
          :options="presetOptionsWithAction"
          :searchable="false"
          @change="onPresetChange"
          :disabled="!hasSharingPermission"
        >
          <template #suffix="{ option }">
            <BittsButton
              v-if="option.value !== 'custom'"
              class="c-sharing-settings-item__preset-details-btn"
              variant="ghost"
              type="neutral"
              size="small"
              :center-icon="getPresetSuffixIcon(option.value)"
              @click="(e) => handleOpenPresetModal(e, option.value)"
            />
          </template>
        </BittsSelect>
      </div>
    </div>
    <router-view-wrapper />
  </BittsModalTwo>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsModalTwo,
  BittsSelect,
  BittsSwitch,
} from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { isEqual } from 'lodash';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import {
  SHARING_TYPES,
  SharingType,
  sharingSelectOptions,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { useDataSharesStore } from '@/stores';
import { Population } from '@/types/populations';

import useSharingSettings, {
  PopulationOptionItem,
} from './SettingsModal/SharingSettingsTab/composables/useSharingSettings';

type Props = {
  parentId: number;
  population: Population;
  onClose: () => void;
};

const { parentId, population, onClose } = defineProps<Props>();

const router = useRouter();
const route = useRoute();
const { hasPermission } = useAuth();
const { refreshDataSharesStore } = useDataSharesStore();

const {
  isLoading: initialLoading,
  dataShares,
  presetOptions: allPresets,
  populationOptions,
  showSuccessToast,
} = useSharingSettings();

const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

const isLoading = ref<boolean>(true);
const selectedSharing = ref<SharingType>(SHARING_TYPES.HIDDEN);
const selectedPresetId = ref<number | null>(null);
const defaultPresetId = ref<number | null>(null);
const initialSelections = ref({
  sharing: SHARING_TYPES.HIDDEN as SharingType,
  presetId: null as number | null,
});
const presetSelectRef = ref<InstanceType<typeof BittsSelect> | null>(null);
const isOpenFieldPresetsModal = ref(false);

const presetOptions = computed(() => allPresets.value[parentId] || []);
const presetOptionsWithAction = computed(() => [
  ...presetOptions.value,
  {
    heading: null,
    items: [
      {
        value: 'custom',
        label: 'Create field preset',
        icon: ['far', 'plus'] as [BittsIconPrefix, string],
      },
    ],
  },
]);

const isNewPop = computed(() => route.query?.newPopulation);
const isShared = computed(() => selectedSharing.value !== SHARING_TYPES.HIDDEN);
const isOverlapCounts = computed(
  () => selectedSharing.value === SHARING_TYPES.OLAPS_ONLY,
);
const title = computed(() => {
  return `Define your Sharing Default ${population?.name ? `for ${population?.name}` : ''}`;
});

const dataShare = computed(() =>
  dataShares.value.find((rule) => rule.populationId === population.id),
);

const hasChanges = computed(
  () =>
    !isEqual(selectedSharing.value, initialSelections.value.sharing) ||
    !isEqual(selectedPresetId.value, initialSelections.value.presetId),
);

function toggleShared(checked: boolean) {
  if (checked) {
    selectedSharing.value =
      initialSelections.value.sharing === SHARING_TYPES.HIDDEN
        ? SHARING_TYPES.SHARING
        : initialSelections.value.sharing;
    selectedPresetId.value =
      initialSelections.value.presetId ?? defaultPresetId.value;
  } else {
    selectedSharing.value = SHARING_TYPES.HIDDEN;
    selectedPresetId.value = null;
  }
}

function onVisibilityChange(newVisibility: SharingType) {
  selectedSharing.value = newVisibility;
  if (newVisibility === SHARING_TYPES.OLAPS_ONLY) {
    selectedPresetId.value = null;
  } else if (selectedPresetId.value === null) {
    selectedPresetId.value =
      initialSelections.value.presetId ?? defaultPresetId.value;
  }
}

function onPresetChange(newPresetId: 'custom' | number | null) {
  // View/edit existing preset
  if (isOpenFieldPresetsModal.value) return;

  // Create new preset
  if (newPresetId === 'custom') {
    handleOpenCreatePresetModal();
    return;
  }

  selectedPresetId.value = newPresetId;
}

async function handleClose() {
  if (isNewPop.value) {
    await router.push({
      name: 'edit_population',
      params: {
        population_id: population.id,
      },
    });
  } else {
    onClose();
  }
}

async function handleSave() {
  if (!selectedSharing.value) return;
  const { error } = await crossbeamApi.POST('/v0.1/data-share-assignments', {
    body: {
      partner_org_id: null,
      data_shares: [
        {
          population_id: population.id,
          visibility: selectedSharing.value,
          preset_id: selectedPresetId.value,
        },
      ],
    },
  });
  if (error) throw error;
  await refreshDataSharesStore();
  showSuccessToast();
  handleClose();
}

watch([dataShare, initialLoading], ([existingRule, loading]) => {
  if (loading) return;
  if (existingRule) {
    selectedSharing.value = existingRule.visibility;
    selectedPresetId.value = existingRule.presetId ?? null;
    initialSelections.value = {
      sharing: existingRule.visibility,
      presetId: existingRule.presetId ?? null,
    };
  } else {
    defaultPresetId.value =
      populationOptions.value.find(
        (option: PopulationOptionItem) => option.value === population.id,
      )?.defaultPresetId ?? null;
    if (isNewPop.value) {
      // When creating a new population, we prefill default sharing
      selectedSharing.value = SHARING_TYPES.SHARING;
      selectedPresetId.value = defaultPresetId.value;
    }
  }
  isLoading.value = false;
});

// Following is duplicated from SharingItems.vue
// Needs to be refactored when we have a better solution for preset modals
// ticket: https://app.shortcut.com/crossbeam/story/60938/refactor-edit-create-preset-dialogs

// Triggered when preset is created/edited and its data being passed as query in the router.
watch(
  () => [route?.query.newPresetId, route?.query.presetId],
  ([newPresetId, presetId]) => {
    const id = newPresetId || presetId;
    if (id) {
      onPresetChange(Number(id));
      const newQuery = { ...router.currentRoute.value.query };
      delete newQuery.newPresetId;
      delete newQuery.presetId;
      delete newQuery.populationId;
      router.replace({ query: newQuery });
    }
    isOpenFieldPresetsModal.value = false;
  },
);

const sourceIdParam = isGoogleOrCsvSource(String(population.integration_type))
  ? population.source_id
  : undefined;

function handleOpenPresetModal(event: Event, presetId: number) {
  isOpenFieldPresetsModal.value = true;

  // Ensures dropdown removal without selecting related preset
  presetSelectRef.value?.instantBlur();
  event.stopPropagation();
  event.preventDefault();

  router.push({
    name: getRouteName('preset-details'),
    params: {
      feed_id: population.feed_id,
      source_id: sourceIdParam,
      preset_id: presetId,
    },
    query: route.query,
  });
}

function handleOpenCreatePresetModal() {
  router.push({
    name: getRouteName('create-preset'),
    params: {
      feed_id: population.feed_id,
      source_id: sourceIdParam,
    },
    query: route.query,
  });
}

function getPresetSuffixIcon(presetId?: number) {
  if (!presetId) return null;

  const defaultPresetOption = presetOptions.value
    ?.flatMap((group) => group.items)
    .find((item) => item.label === 'Crossbeam recommended preset');

  const isCurrentOptionDefaultPreset = defaultPresetOption?.value === presetId;

  return [
    'far',
    hasSharingPermission.value && !isCurrentOptionDefaultPreset ? 'pen' : 'eye',
  ] as [BittsIconPrefix, string];
}

function getRouteName(suffix: string) {
  if (route.name === 'edit_population__sharing') {
    return `population-sharing-${suffix}`;
  }
  return `populations-sharing-${suffix}`;
}
</script>
